/**
 * Copy the funnel repeats across screens: reviews and product screenshots.
 *
 * Both live behind `t()` so a language switch moves them with everything else,
 * and both are lists so a screen never hardcodes "the second review".
 */
import { TFn } from '../i18n/useT';
import { Review } from './ui';

/**
 * Reviews shown on 01 · Analyzing and on the paywall.
 *
 * Supplied by the product owner, in both languages, and treated as real
 * customer statements. They must stay attributable: publishing a review from
 * someone who did not give it is a civil-penalty offence in the US under the
 * FTC's rule on consumer reviews (16 CFR Part 465) and a banned practice in
 * the EU. Edit the wording, never the fact that a named person said it.
 *
 * Removing one needs no code change: blank `pw.review.N.title` in the locale
 * bundle and it disappears; blank them all and the whole section goes, because
 * every caller already handles an empty list.
 */
export const REVIEW_COUNT = 3;

export function reviews(t: TFn): Review[] {
    return Array.from({ length: REVIEW_COUNT }, (_, i) => {
        const n = i + 1;
        const role = t(`pw.review.${n}.role`);

        return {
            title: t(`pw.review.${n}.title`),
            quote: t(`pw.review.${n}.quote`),
            name: t(`pw.review.${n}.name`),
            age: Number(t(`pw.review.${n}.age`)) || 0,
            // A missing key resolves to the key itself; treat that as absent.
            role: role.startsWith('pw.review.') ? '' : role,
        };
    }).filter((r) => r.title && !r.title.startsWith('pw.review.'));
}

/** The product screens we hold renders of. Not every screen exists in both sizes. */
export type ScreenKey = 'state' | 'directions' | 'pattern' | 'task';

export interface Shot {
    src: string;
    title: string;
    sub: string;
    /** Which product screen this is, so a phone and a laptop render can be paired. */
    screen: ScreenKey;
}

const asset = (name: string): string => `${process.env.PUBLIC_URL || ''}/images/paywall/${name}`;

/**
 * Phone screenshots of the real product.
 *
 * `ASPECT` is measured from the files (1320×2610 after the browser chrome was
 * cropped), not assumed — a hardcoded ratio leaves a dead strip under the app's
 * own tab bar inside the phone frame.
 */
export const SHOT_ASPECT = 1320 / 2610;

const shot = (t: TFn, file: string, screen: ScreenKey): Shot => ({
    src: asset(file),
    screen,
    title: t(`pw.pay.shot.${screen}.title`),
    sub: t(`pw.pay.shot.${screen}.sub`),
});

export function phoneShots(t: TFn): Shot[] {
    return [
        shot(t, 'shot-state.png', 'state'),
        shot(t, 'shot-directions.png', 'directions'),
        shot(t, 'shot-pattern.png', 'pattern'),
    ];
}

/**
 * The product on a wide window.
 *
 * The captions follow what each render actually contains — the three files
 * arrived named one position out of step and were renamed to match, because a
 * caption naming the wrong screen is worse than none.
 */
export function desktopShots(t: TFn): Shot[] {
    return [
        shot(t, 'desk-state.png', 'state'),
        shot(t, 'desk-patterns.png', 'pattern'),
        shot(t, 'desk-task.png', 'task'),
    ];
}

export interface ShotPair {
    phone: Shot;
    laptop: Shot;
    /** Both renders are of the same product screen. */
    matched: boolean;
}

/**
 * Phone and laptop renders side by side, paired by the screen they show.
 *
 * Pairing by position would be wrong: the two sets overlap but are not the same
 * list — there is no laptop render of Directions and no phone render of a Task,
 * so index 2 would put a pattern next to a task and caption them as one thing.
 * Matching screens pair first; whatever is left over is paired off in order and
 * simply carries its own caption on each side, which stays true.
 */
export function shotPairs(t: TFn): ShotPair[] {
    const phones = phoneShots(t);
    const laptops = desktopShots(t);
    const usedPhone = new Set<ScreenKey>();
    const usedLaptop = new Set<ScreenKey>();
    const pairs: ShotPair[] = [];

    phones.forEach((phone) => {
        const laptop = laptops.find((l) => l.screen === phone.screen);

        if (!laptop) return;
        usedPhone.add(phone.screen);
        usedLaptop.add(laptop.screen);
        pairs.push({ phone, laptop, matched: true });
    });

    const restPhones = phones.filter((p) => !usedPhone.has(p.screen));
    const restLaptops = laptops.filter((l) => !usedLaptop.has(l.screen));

    for (let i = 0; i < Math.min(restPhones.length, restLaptops.length); i += 1) {
        pairs.push({ phone: restPhones[i], laptop: restLaptops[i], matched: false });
    }

    return pairs;
}
